import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Award, Clock, Check, Lock, ArrowRight, Ticket } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import TopNav from "@/components/TopNav";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [promo, setPromo] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => { try { const { data } = await api.get("/courses"); setCourses(data); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(); }, []);
  // Re-fetch on tab focus / visibility so a price change by Super Admin reflects
  // immediately for already-logged-in students without a manual refresh.
  useEffect(() => {
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const enroll = async (usePromo) => {
    setBusy(true);
    try {
      const body = usePromo ? { promo_code: promo } : { pay: true };
      const { data } = await api.post(`/courses/${selected.id}/enroll`, body);
      if (data.enrolled) { toast.success(data.message); setSelected(null); setPromo(""); load(); nav(`/courses/${selected.id}`); }
      else toast.info(data.message, { duration: 7000 });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <TopNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-24">
        <span className="pill text-[11px] tracking-[0.2em] px-4 py-1.5 glass text-teal-accent inline-block">CERTIFICATE TRACKS</span>
        <h1 className="serif text-4xl sm:text-5xl font-extrabold mt-5">Courses &amp; certifications</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">Ten professional 90-day tracks. Complete one to earn a certificate and unlock the career board.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {courses.map((c) => {
            const pct = Math.round((c.completed_days / 90) * 100);
            return (
              <button key={c.id} data-testid={`course-${c.key}`}
                      onClick={() => c.enrolled ? nav(`/courses/${c.id}`) : setSelected(c)}
                      className="glass glass-hover rounded-3xl p-6 text-left relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <span className={`pill text-[11px] px-3 py-1 flex items-center gap-1.5 ${c.enrolled ? "bg-[#00D4D4]/15 text-teal-accent" : "glass text-muted-foreground"}`}>
                    {c.enrolled ? <><Check size={12} /> ENROLLED</> : <><Award size={12} /> CERTIFICATE</>}
                  </span>
                  <GraduationCap size={26} className="text-teal-accent/60" />
                </div>
                <h3 className="serif text-2xl font-bold leading-tight">{c.title}</h3>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.description}</p>
                <div className="flex items-center gap-4 mt-5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock size={13} /> 90 Days</span>
                  <span>₹4,999</span>
                </div>
                {c.enrolled && (
                  <div className="mt-4">
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#00D4D4]" style={{ width: `${pct}%` }} /></div>
                    <div className="text-[11px] text-muted-foreground mt-1.5">{c.completed_days}/90 days · {pct}%</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setSelected(null)}>
          <div className="glass-strong rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="enroll-modal">
            <h3 className="serif text-3xl font-bold">{selected.title}</h3>
            <p className="text-sm text-muted-foreground mt-2">{selected.description}</p>
            <div className="space-y-3 mt-5">
              <Row icon={Clock} label="Duration" value="90 Days" />
              <Row icon={Award} label="Certificate" value="Provided on completion" />
              <Row icon={Ticket} label="Course fee" value="₹4,999" />
            </div>
            <div className="mt-6">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Promo code</label>
              <div className="flex gap-2 mt-2">
                <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)}
                       placeholder="Enter code" className="kiq-input flex-1 px-4 py-3 text-sm" />
                <button data-testid="apply-promo" disabled={busy || !promo} onClick={() => enroll(true)} className="kiq-btn px-5 text-sm">Apply</button>
              </div>
            </div>
            <button data-testid="pay-now" disabled={busy} onClick={() => enroll(false)}
                    className="w-full glass hover:border-[#00D4D4]/40 rounded-full py-3.5 mt-4 text-sm flex items-center justify-center gap-2">
              Pay Now <ArrowRight size={16} />
            </button>
            <p className="text-[11px] text-muted-foreground text-center mt-3">Try promo <b className="text-teal-accent">KIQHUB100</b> to unlock instantly.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between glass rounded-xl px-4 py-3">
      <span className="text-sm text-muted-foreground flex items-center gap-2"><Icon size={15} className="text-teal-accent" /> {label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
